import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import ToggleButton from '@material-ui/lab/ToggleButton';
import { observer } from 'mobx-react-lite';
import React, { useContext, useState } from 'react';
import ChipContainer from '../../../common/ChipContainer';
import LoadingIndicator from '../../../common/LoadingIndicator';
import MobxContext from '../../../mobx/MobxContext';
import WorkflowAccessPermissions from './WorkflowAccessPermissions';
import WorkflowDeploymentActionExpansionPanel from './WorkflowDeploymentActionExpansionPanel';
import WorkflowNotfications from './WorkflowNotifications';
import WorkflowSettings from './WorkflowSettings';
import WorkflowTestActionExpansionPanel from './WorkflowTestActionExpansionPanel';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionHeader: {
      marginBottom: 8,
    },
    largeGutter: {
      marginBottom: 16
    },
    actionsSectionHeader: {
      marginBottom: 8,
      display: 'flex',
      justifyContent: 'space-between'
    },
    section: {
      marginBottom: 32
    },
    panel: {
      padding: 24,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
    },
    actionListHeaderTableCell: {
      borderBottom: 'none'
    },
    actionListHeaderTable: {
      tableLayout: 'fixed'
    },
    actionListHeaderContainer: {
      padding: '0 64px 0 24px'
    },
    paramSetContainer: {
      display: 'flex'
    },
    paramSetLink: {
      marginRight: 8
    },
    tableHead: {
      backgroundColor: 'inherit'
    }
  })
);

interface Props {
  workflow: any;
  family: any;
}

function WorkflowDetailsTab(props: Props) {
  const { workflow, family } = props;
  const classes = useStyles({});

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(true);
  const { cspGroupStore, environmentStore } = useContext(MobxContext);

  if (workflow && family && cspGroupStore.groups && environmentStore.environments) {
    const environment = workflow.AccountName ? environmentStore.getEnvironmentByName(workflow.AccountName) : null;
    const accountId = environment ? environment.AccountID : null;
    return (
      <div>
        {workflow.DeploymentRecipe.length > 0 && <div className={classes.section}>
          <div className={classes.actionsSectionHeader}>
            <Typography color="textSecondary" variant="h6" gutterBottom>Deployment Actions</Typography>
            <ToggleButton
              value="true"
              selected={!showAdvancedSettings}
              onChange={() => setShowAdvancedSettings(!showAdvancedSettings)}
              size="small"
            >
              {showAdvancedSettings ? 'Hide Action Fields' : 'Show Action Fields'}
            </ToggleButton>
          </div>
          <div className={classes.actionListHeaderContainer}>
            <Table size="small" className={classes.actionListHeaderTable} padding="none">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  {showAdvancedSettings && <TableCell className={classes.actionListHeaderTableCell}>
                    Action
                      </TableCell>}
                  <TableCell className={classes.actionListHeaderTableCell}>
                    Deck
                      </TableCell>
                  <TableCell className={classes.actionListHeaderTableCell}>
                    Deployment
                      </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </div>
          <div>
            {workflow.DeploymentRecipe.map(action => <WorkflowDeploymentActionExpansionPanel
              key={action.ActionName}
              action={action}
              accountId={accountId}
              region={workflow.Region}
              showAdvancedSettings={showAdvancedSettings}
            />)}
          </div>
        </div>}
        {workflow.TestFunctions && workflow.TestFunctions.length > 0 && <div className={classes.section}>
          <div className={classes.actionsSectionHeader}>
            <Typography color="textSecondary" variant="h6" gutterBottom>Test Actions</Typography>
          </div>
          <div>
            <div className={classes.actionListHeaderContainer}>
              <Table size="small" className={classes.actionListHeaderTable} padding="none">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.actionListHeaderTableCell}>
                      Action
                        </TableCell>
                    <TableCell className={classes.actionListHeaderTableCell}>
                      Test
                        </TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </div>
            <div>
              {workflow.TestFunctions.map(action => <WorkflowTestActionExpansionPanel
                key={action.ActionName}
                action={action}
                accountId={accountId}
                region={workflow.Region}
                showAdvancedSettings={showAdvancedSettings}
              />)}
            </div>
          </div>
        </div>}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography color="textSecondary" variant="h6" gutterBottom>Action Defaults</Typography>
          </div>
          <div className={classes.panel}>
            <div>
              <div className={classes.largeGutter}>
                <Typography variant="subtitle1">Default IAM Deployment Role</Typography>
                <Typography color="textSecondary">{workflow.DefaultDeploymentIAMRole || 'None'}</Typography>
              </div>
              <div>
                <Typography variant="subtitle1">Default Parameter Set</Typography>
                {(!workflow.DefaultParameterSet || !workflow.DefaultParameterSet.Name) && <Typography>None</Typography>}
                {workflow.DefaultParameterSet && workflow.DefaultParameterSet.Name !== undefined && <div className={classes.paramSetContainer}>
                  <Link
                    variant="body1"
                    className={classes.paramSetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`/faro/parameters/${accountId}/${workflow.Region}/${workflow.DefaultParameterSet.Name}?version=${workflow.DefaultParameterSet.Version}`}
                  >
                    {workflow.DefaultParameterSet.Name}
                  </Link>
                  <Chip label={workflow.DefaultParameterSet.Version} variant="outlined" size="small" />
                </div>}
              </div>
            </div>
          </div>
        </div>
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography color="textSecondary" variant="h6">Access Permissions</Typography>
          </div>
          <div className={classes.panel}>
            <Typography variant="caption" display="block" className={classes.largeGutter}>
              Assign workflow permissions to AWS toolbox groups. Group ID number is in parentheses.
            </Typography>
            <WorkflowAccessPermissions workflow={workflow} cspGroups={cspGroupStore.groups} />
          </div>
        </div>
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography color="textSecondary" variant="h6" gutterBottom>Settings</Typography>
          </div>
          <div className={classes.panel}>
            <WorkflowSettings workflow={workflow} />
          </div>
        </div>
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography color="textSecondary" variant="h6">Notifications</Typography>
          </div>
          <div className={classes.panel}>
            <WorkflowNotfications workflow={workflow} />
          </div>
        </div>
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography color="textSecondary" variant="h6">Custom Approvers</Typography>
            <Typography variant="caption">
              A user in each Custom Approvers group must approve before every execution of this workflow. Windsor may also request additional approvers depending on the IAM roles specified in the "Actions" section. Group ID number is in parentheses.
          </Typography>
          </div>
          {workflow.Approvers && workflow.Approvers.PreApprovers && <ChipContainer>
            {workflow.Approvers.PreApprovers.map(group => <Chip key={group} label={cspGroupStore.groups[group]} color="primary" />)}
          </ChipContainer>}
          {(!workflow.Approvers || !workflow.Approvers.PreApprovers || workflow.Approvers.PreApprovers.length === 0) && <Typography color="textSecondary">None</Typography>}
        </div>
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography color="textSecondary" variant="h6">Custom Faro Deck Tags</Typography>
            <Typography variant="caption">
              List of tags to apply to decks upon successful workflow execution
            </Typography>
          </div>
          {workflow.CustomTags && <ChipContainer>
            {workflow.CustomTags.map(tag => <Chip key={tag} label={tag} color="primary" />)}
          </ChipContainer>}
          {(!workflow.CustomTags || workflow.CustomTags.length === 0) && <Typography color="textSecondary">None</Typography>}
        </div>
      </div >
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }
};

export default observer(WorkflowDetailsTab);
